import { Icon } from "./Common.jsx";
import { requestAmountLabel, requestWhenLabel, requestWhoLabel } from "./RequestsTab.jsx";
import { HW_SHARED_TYPE, isHomeworkRequest } from "../lib/homework.js";
import { t as ct } from "../lib/content.js";

// The waiting-requests overlay (Alan, 2 Oct). Shown over Home as a parent enters Parent Mode
// from Kid Mode, when something is waiting that this device has not already shown; App.jsx
// decides when, and what has been shown. "The primary (not only) purpose of this overlay is to
// alert the user to homework requests waiting for review", and every other waiting request is
// listed too, "as a navigation aid to get to where they need to go". It is a SIGNPOST: nothing
// is approved or refused in it. A row takes the parent to the request; See all requests to the
// tab; Not now (or the dimmed page, or back) closes it.
//
// It replaced CarriedHomeworkPrompt (LB-382, 1 Oct), whose job (telling a family who signed up
// from a shared homework that it is waiting) is now one row here, with the "Tried before you
// signed up" line.
//
// Rows are described by the Requests tab's own helpers, so the two can never word a request
// differently. Homework first (shared, and a sheet a child photographed), then the rest, oldest
// first within each; five rows at most, then "and {n} more".
//
// Props are defaulted because the screen is registered by component for Content Studio, which
// renders it with nothing passed.
export const REQUESTS_OVERLAY_MAX_ROWS = 5;

export function overlayOrder(requests) {
  const when = (r) => Date.parse(r.timestamp || "") || 0;
  const pending = (requests || []).filter((r) => r && r.status === "pending");
  const hw = pending.filter(isHomeworkRequest).sort((a, b) => when(a) - when(b));
  const rest = pending.filter((r) => !isHomeworkRequest(r)).sort((a, b) => when(a) - when(b));
  return [...hw, ...rest];
}

export function RequestsOverlay({ requests = [], profiles = [], rate = 10, onOpen = () => {}, onSeeAll = () => {}, onLater = () => {} }) {
  const ordered = overlayOrder(requests);
  const shown = ordered.slice(0, REQUESTS_OVERLAY_MAX_ROWS);
  const more = ordered.length - shown.length;
  const nameOf = (id) => profiles.find((p) => p.id === id)?.name ?? "Unknown";
  const n = ordered.length;
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/45 flex items-center justify-center p-4" role="dialog" aria-label={n === 1 ? ct("requests_overlay.title_one", "1 request waiting") : ct("requests_overlay.title_many", "{n} requests waiting", { n })} onClick={onLater} data-testid="requests-overlay">
      <div className="bg-white rounded-lg w-full max-w-sm p-5 max-h-full overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="text-lg font-extrabold text-slate-900">{n === 1 ? ct("requests_overlay.title_one", "1 request waiting") : ct("requests_overlay.title_many", "{n} requests waiting", { n })}</div>
        <p className="text-sm text-slate-500 mt-1">{ct("requests_overlay.body", "Tap one to go straight to it.")}</p>
        <div className="border border-slate-200 rounded-lg overflow-hidden mt-3">
          {shown.map((r) => {
            const amount = requestAmountLabel(r, rate);
            const kicker = r.requestType === HW_SHARED_TYPE ? ct("requests_overlay.homework_kicker", "Homework to check") : requestWhoLabel(r, nameOf);
            return (
              <button key={r.id} type="button" onClick={() => onOpen(r)} className="w-full text-left px-3 py-2.5 border-b border-slate-100 last:border-b-0 flex items-center gap-3 active:bg-slate-50" data-testid={`requests-overlay-row-${r.id}`} data-request-type={r.requestType}>
                <span className="flex-1 min-w-0">
                  {r.requestType === HW_SHARED_TYPE ? (
                    <>
                      <span className="block text-sm font-bold text-slate-900 break-words">{r.title}</span>
                      <span className="block text-xs text-slate-500">{kicker}</span>
                    </>
                  ) : (
                    <>
                      <span className="flex items-center justify-between gap-2">
                        <span className="text-xs font-medium text-slate-500 truncate">{kicker}</span>
                        {amount && <span className="text-xs font-bold text-amber-600 flex-shrink-0">{amount}</span>}
                      </span>
                      <span className="block text-sm font-semibold text-slate-900 break-words">{r.title}</span>
                    </>
                  )}
                  {r.carried && (
                    <span className="block text-xs text-slate-500" data-testid="requests-overlay-carried">{(r.guestOinks || 0) > 0
                      ? ct("requests_overlay.carried_oinks", "Tried before you signed up · {n} Oinks to claim", { n: r.guestOinks })
                      : ct("requests_overlay.carried", "Tried before you signed up")}</span>
                  )}
                  <span className="block text-xs text-slate-400">{requestWhenLabel(r)}</span>
                </span>
                <Icon id="ic-forward" size={16} className="text-slate-400 flex-shrink-0" style={{ color: "currentColor" }} />
              </button>
            );
          })}
        </div>
        {more > 0 && <p className="text-xs text-slate-500 mt-2 px-1" data-testid="requests-overlay-more">{ct("requests_overlay.more", "and {n} more", { n: more })}</p>}
        <button type="button" onClick={onSeeAll} className="w-full mt-4 bg-slate-900 text-white text-sm font-bold rounded-lg py-3" data-testid="requests-overlay-see-all">{ct("requests_overlay.see_all", "See all requests")}</button>
        <button type="button" onClick={onLater} className="w-full mt-2 bg-white border border-slate-200 text-slate-900 text-sm font-bold rounded-lg py-3" data-testid="requests-overlay-later">{ct("requests_overlay.later", "Not now")}</button>
      </div>
    </div>
  );
}
